import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { UnpairIcon } from "./icons";
import { ProductImage } from "./ui/ProductImage";

export interface PrivateCollectionItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Product image URL
   */
  productImage: string;
  /**
   * Brand name (e.g., "ANYW3AR")
   */
  brandName: string;
  /**
   * Product name (e.g., "Pigalle Denim Jacket - Black")
   */
  productName: string;
  /**
   * NFC chip ID
   */
  nfcId: string;
  /**
   * Product edition number
   */
  productNumber: {
    current: number;
    total: number;
  };
  /**
   * Product size (e.g., "XL")
   */
  productSize: string;
  /**
   * Callback when unpair button is clicked
   */
  onUnpair: () => void;
  /**
   * Optional partner link configuration
   */
  partnerLink?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Visibility state for the item (optional - hides toggle if not provided)
   */
  isVisible?: boolean;

  /**
   * Visibility state for the item (optional - hides toggle if not provided)
   */
  unPairBtnHidden?: boolean;
  /**
   * Callback when visibility toggle changes (optional - hides toggle if not provided)
   */
  onVisibilityChange?: (visible: boolean) => void;
}

/**
 * Private Collection Item component for owner's Trophy Room
 *
 * Displays product details with image, NFC ID, edition number, size,
 * and actions (unpair, optional partner link, visibility toggle).
 *
 * @example
 * ```tsx
 * <PrivateCollectionItem
 *   productImage="/images/jacket.jpg"
 *   brandName="ANYW3AR"
 *   productName="Pigalle Denim Jacket - Black"
 *   nfcId="adnoa46z5vr1z35ef"
 *   productNumber={{ current: 24, total: 333 }}
 *   productSize="XL"
 *   onUnpair={() => console.log('Unpair clicked')}
 *   partnerLink={{ label: "PARTNER LINK", onClick: () => {} }}
 *   isVisible={true}
 *   onVisibilityChange={(visible) => console.log(visible)}
 * />
 * ```
 */
export const PrivateCollectionItem = React.forwardRef<
  HTMLDivElement,
  PrivateCollectionItemProps
>(
  (
    {
      className,
      productImage,
      brandName,
      productName,
      nfcId,
      productNumber,
      productSize,
      onUnpair,
      partnerLink,
      isVisible,
      unPairBtnHidden = false,
      onVisibilityChange,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn("flex gap-[1rem] items-start w-full", className)}
        {...props}
      >
        {/* Product Image */}
        <ProductImage
          src={productImage}
          alt={productName}
          containerClassName="flex-1 min-h-px min-w-px relative self-stretch shrink-0"
        />

        {/* Product Details */}
        <div className="flex flex-1 flex-col gap-[1rem] items-start min-h-px min-w-px shrink-0">
          {/* Brand Name */}
          <div className="flex flex-col font-montserrat font-normal justify-end leading-[0] shrink-0 text-[0.875rem] text-gray-500 whitespace-nowrap">
            <p className="leading-[1.25rem]">{brandName}</p>
          </div>

          {/* Product Name */}
          <div className="flex flex-col font-montserrat font-bold justify-end leading-[0] min-w-full shrink-0 text-[0.9375rem] text-black w-min">
            <p className="leading-[1.25rem] whitespace-pre-wrap">
              {productName}
            </p>
          </div>

          {/* Product Info Grid */}
          <div className="flex flex-col gap-[1rem] items-start shrink-0 w-full">
            {/* NFC ID */}
            <div className="flex flex-col font-montserrat font-normal gap-[0.5rem] items-start leading-[0] shrink-0 w-full">
              <div className="flex flex-col justify-end shrink-0 text-[0.75rem] text-gray-500 whitespace-nowrap">
                <p className="leading-[1.25rem]">NFC ID :</p>
              </div>
              <div className="flex flex-col justify-end min-w-full shrink-0 text-[1rem] text-black tracking-[0.125rem] w-min">
                <p className="leading-[1.25rem] whitespace-pre-wrap">{nfcId}</p>
              </div>
            </div>

            {/* Product Number */}
            <div className="flex flex-col font-montserrat font-normal gap-[0.5rem] items-start leading-[0] shrink-0 w-full whitespace-nowrap">
              <div className="flex flex-col justify-end shrink-0 text-[0.75rem] text-gray-500">
                <p className="leading-[1.25rem]">Number</p>
              </div>
              <div className="flex flex-col justify-end shrink-0 text-[0px] text-black">
                <p className="leading-[1.25rem]">
                  <span className="font-montserrat font-bold text-[1.25rem] text-black">
                    {productNumber.current}
                  </span>
                  <span className="text-[0.9375rem]">
                    /{productNumber.total}
                  </span>
                </p>
              </div>
            </div>

            {/* Size */}
            <div className="flex flex-col font-montserrat font-normal gap-[0.5rem] items-start leading-[0] shrink-0 w-full whitespace-nowrap">
              <div className="flex flex-col justify-end shrink-0 text-[0.75rem] text-gray-500">
                <p className="leading-[1.25rem]">Size</p>
              </div>
              <div className="flex flex-col justify-end shrink-0 text-[0.9375rem] text-black">
                <p className="leading-[1.25rem]">{productSize}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-[0.5rem] items-start shrink-0 w-full">
            {/* Unpair Button */}
            {!unPairBtnHidden && (
              <Button
                variant="compact-gray"
                size="compact"
                onClick={onUnpair}
                leftIcon={<UnpairIcon size={10} />}
                className="h-[1.25rem]"
              >
                UNPAIR
              </Button>
            )}

            {/* Partner Link Button (Story 3.7.b) */}
            {partnerLink && (
              <Button
                variant="compact-red"
                size="compact"
                onClick={partnerLink?.onClick}
                className="h-[1.25rem]"
              >
                {partnerLink?.label}
              </Button>
            )}

            {/* Visibility Toggle (only shown if props provided) */}
            {isVisible !== undefined && onVisibilityChange && (
              <div className="flex items-center gap-[0.5rem] shrink-0">
                <Switch
                  checked={isVisible}
                  onCheckedChange={onVisibilityChange}
                />
                <span className="font-montserrat font-normal text-[0.75rem] text-gray-500">
                  Show in my public collection
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

PrivateCollectionItem.displayName = "PrivateCollectionItem";
